// NEW_FILE_JUSTIFIED: Review step for policy application wizard — displays summary cards per section with Edit/Fix Issues buttons before final submission
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Pencil, ArrowLeft, Send, Check, AlertCircle } from 'lucide-react';
import type { IReviewStepProps, IReviewSectionItem } from '@/@types/policyFormTemplates';

function ReviewCard({ section, onEdit }: { section: IReviewSectionItem; onEdit: () => void }) {
  const hasError = section.hasError === true;

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-xl bg-white px-6 py-5 border',
        hasError ? 'border-red-500 border-[1.5px]' : 'border-zinc-200'
      )}
    >
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]',
            hasError ? 'bg-red-50' : 'bg-lime-100'
          )}
        >
          {hasError ? (
            <AlertCircle className="h-5 w-5 text-red-500" />
          ) : (
            <Check className="h-5 w-5 text-lime-600" />
          )}
        </div>
        <div className="flex flex-col gap-0.5">
          <span
            className={cn('text-sm font-semibold', hasError ? 'text-red-500' : 'text-zinc-900')}
          >
            {section.title}
          </span>
          <span className="text-xs text-zinc-500">{section.summary}</span>
        </div>
        {hasError && section.errorMessage && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2.5 py-0.5 text-xs font-medium text-red-600">
            <AlertCircle className="h-3 w-3" />
            {section.errorMessage}
          </span>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onEdit}
        className={cn(
          'gap-1.5',
          hasError ? 'border-red-200 text-red-500 hover:bg-red-50' : 'border-zinc-200 text-zinc-500'
        )}
      >
        <Pencil className="h-3.5 w-3.5" />
        {hasError ? 'Fix Issues' : 'Edit'}
      </Button>
    </div>
  );
}

export function ReviewStep({
  sections,
  onEditSection,
  onSubmit,
  onBack,
  isSubmitting,
}: IReviewStepProps) {
  const hasAnyErrors = sections.some(s => s.hasError);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-zinc-900">Review Your Application</h2>
        <p className="text-sm text-zinc-500">
          Please review all information before submitting. Click Edit on any section to make
          changes.
        </p>
      </div>

      {sections.map(section => (
        <ReviewCard
          key={section.key}
          section={section}
          onEdit={() => onEditSection(section.stepIndex)}
        />
      ))}

      <div className="flex items-center justify-between pt-3">
        <Button type="button" variant="outline" onClick={onBack} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting || hasAnyErrors}
          data-testid="claim-wizard-submit"
          className={cn(
            'gap-2.5 border-0 font-semibold px-8 py-3 text-[15px]',
            hasAnyErrors
              ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
              : 'bg-lime-200 text-zinc-900 hover:bg-lime-300 shadow-[0_0_8px_rgba(132,204,22,0.27)]'
          )}
        >
          <Send className="h-[18px] w-[18px]" />
          {isSubmitting ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </div>
    </div>
  );
}
